import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatDistanceToNow } from 'date-fns';

const TYPE_META = {
  urgent:  { badge: 'badge-red',    label: 'Urgent'  },
  warning: { badge: 'badge-yellow', label: 'Warning' },
  bedtime: { badge: 'badge-purple', label: 'Bedtime' },
  info:    { badge: 'badge-blue',   label: 'Info'    },
};

const FILTERS = [
  { key: 'all',      label: 'All'      },
  { key: 'unread',   label: 'Unread'   },
  { key: 'urgent',   label: 'Urgent'   },
  { key: 'warnings', label: 'Warnings' },
];

export default function Alerts({ kidId, parentId, deviceFilter }) {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // Fetch alerts — scoped to the currently selected kid if one is set.
  const fetchAlerts = async () => {
    if (!parentId) return;
    setLoading(true);
    setProgress(20);
    let query = supabase
      .from('alerts')
      .select('*')
      .eq('parent_id', parentId);
    if (kidId) query = query.eq('kid_id', kidId);
    if (deviceFilter && deviceFilter !== 'all') query = query.eq('source', deviceFilter);
    const { data, error } = await query.order('timestamp', { ascending: false });
    setProgress(90);
    if (error) { console.error('Alerts fetch error:', error); }
    setAlerts(data || []);
    setProgress(100);
    setLoading(false);
  };

  useEffect(() => {
    if (!parentId) { setLoading(false); return; }
    fetchAlerts().catch(err => console.error('fetchAlerts error:', err));
  }, [parentId, kidId, deviceFilter]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // Real-time new alerts
  useEffect(() => {
    const ch = supabase
      .channel('alerts-page')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'alerts', filter: `parent_id=eq.${parentId}` }, () => {
        fetchAlerts();
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [parentId]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // Filter client-side
  const filtered = alerts.filter(a => {
    if (filter === 'unread') return !a.is_read;
    if (filter === 'urgent') return a.type === 'urgent';
    if (filter === 'warnings') return a.type === 'warning';
    return true;
  });

  const unreadCount = alerts.filter(a => !a.is_read).length;

  // Actions
  const markAllRead = async () => {
    try {
      let query = supabase.from('alerts').update({ is_read: true })
        .eq('parent_id', parentId).eq('is_read', false);
      if (kidId) query = query.eq('kid_id', kidId);
      if (deviceFilter && deviceFilter !== 'all') query = query.eq('source', deviceFilter);
      const { error } = await query;
      if (error) throw error;
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      showToast('All alerts marked as read');
    } catch (e) {
      console.error('markAllRead error:', e);
      showToast('Failed to mark alerts as read');
    }
  };

  const deleteOne = async (id) => {
    try {
      const { error } = await supabase.from('alerts').delete().eq('id', id);
      if (error) throw error;
      setAlerts(prev => prev.filter(a => a.id !== id));
      showToast('Alert deleted');
    } catch (e) {
      console.error('deleteOne error:', e);
      showToast('Failed to delete alert');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          Alerts{' '}
          {unreadCount > 0 && <span className="badge badge-red">{unreadCount} unread</span>}
        </h1>
        {unreadCount > 0 && (
          <button className="btn" onClick={markAllRead}>Mark all read</button>
        )}
      </div>

      {/* Filter buttons */}
      <div className="filter-bar">
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-icon">{'\uD83D\uDD14'}</div>
          <div className="loading-title">Loading alerts...</div>
          <div className="loading-bar-track">
            <div className="loading-bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="loading-percent">{progress}%</div>
        </div>
      )}

      {/* Alert list */}
      {!loading && (
        <div className="card">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <div className="icon">&#10003;</div>
              <div className="title">
                {filter === 'unread'   ? 'No unread alerts' :
                 filter === 'urgent'   ? 'No urgent alerts' :
                 filter === 'warnings' ? 'No warnings' :
                 'No alerts yet'}
              </div>
              <div className="desc">
                {filter === 'all'
                  ? 'Alerts appear here when flagged content is detected.'
                  : 'Nothing matches this filter.'}
              </div>
            </div>
          ) : (
            filtered.map(alert => {
              const meta = TYPE_META[alert.type] || TYPE_META.info;
              const isPhone = alert.source === 'android';
              return (
                <div
                  key={alert.id}
                  className={`alert-item ${!alert.is_read ? 'unread' : ''}`}
                >
                  <span className={`badge ${meta.badge}`}>{meta.label}</span>
                  <span style={{
                    fontSize: 10, padding: '2px 6px', borderRadius: 4,
                    background: isPhone ? 'rgba(16,185,129,0.15)' : 'rgba(59,130,246,0.15)',
                    color: isPhone ? '#10b981' : '#3b82f6',
                    fontWeight: 700, marginLeft: 4,
                  }}>
                    {isPhone ? '📱' : '💻'}
                  </span>
                  <span className="alert-msg">{alert.message}</span>
                  <span className="alert-time">
                    {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                  </span>
                  <button className="btn-sm btn-danger" onClick={() => deleteOne(alert.id)}>
                    Delete
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Toast */}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
